import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { onOfers, onOffersDelete } from "../../redux/homeAction";
import FullScreenLoader from "../Common/FullScreenLoader";

const OfferList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const getOffersDelete = useSelector((state) => state.home.getOffersDelete);
  const getOffers = useSelector((state) => state.home.getOffers);

  useEffect(() => {
    dispatch(onOfers(setLoader));
  }, [dispatch, getOffersDelete]);

  async function handleDelete(id) {
    if (
      !window.confirm(
        "🔴 Are you sure you want to delete this offer? This cannot be undone.",
      )
    )
      return;
    try {
      dispatch(onOffersDelete(setLoader, id));
    } catch (err) {
      console.error("Error deleting offer", err);
    }
  }

  // Offer Value ko beautifully display karne ke liye helper function
  const renderOfferValue = (offer) => {
    const val = offer.offerValue;
    if (!val) return "-";

    switch (offer.offerType) {
      case "percentage":
        return <span className="fw-bold text-success">{val}% OFF</span>;

      case "flat":
        return <span className="fw-bold text-success">₹{val} OFF</span>;

      case "bogo":
        return (
          <span className="fw-bold text-purple">
            Buy {val.buy || 1} Get {val.get || 1} Free
          </span>
        );

      case "buyXgetY":
        return (
          <span className="fw-bold text-indigo">
            Buy {val.buy} Get {val.get} Deal
          </span>
        );

      case "percentageSlab":
        // Agar dynamic slabs hain to unhe badge format me loop karein
        const slabsArray = val.slabs || (Array.isArray(val) ? val : []);
        if (slabsArray.length === 0) return "-";
        return (
          <div className="d-flex flex-column gap-1">
            {slabsArray.map((slab, index) => (
              <span
                key={index}
                className="badge bg-light text-dark border text-start p-1"
                style={{ fontSize: "11px" }}
              >
                Spend <strong>₹{slab.minSpend}+</strong> →{" "}
                <span className="text-success">
                  {slab.discountPercent}% Off
                </span>
              </span>
            ))}
          </div>
        );

      default:
        return typeof val === "object" ? JSON.stringify(val) : val;
    }
  };

  // Offer Type Badges generator
  const renderTypeBadge = (type) => {
    const badges = {
      percentage: "bg-success-subtle text-success border border-success",
      flat: "bg-primary-subtle text-primary border border-primary",
      bogo: "bg-warning-subtle text-warning-emphasis border border-warning",
      buyXgetY: "bg-info-subtle text-info-emphasis border border-info",
      percentageSlab: "bg-danger-subtle text-danger border border-danger",
    };
    const badgeClass = badges[type] || "bg-secondary text-white";

    // Friendly text mapping
    const labels = {
      percentage: "Percentage",
      flat: "Flat Discount",
      bogo: "BOGO",
      buyXgetY: "Buy X Get Y",
      percentageSlab: "Tiered Slabs",
    };

    return (
      <span className={`badge px-2.5 py-1.5 rounded-pill ${badgeClass}`}>
        {labels[type] || type}
      </span>
    );
  };

  return (
    <div className="container my-4">
      {/* Header Panel */}
      <div className="card border-0 shadow-sm p-4 bg-white rounded-3 mb-4">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="fw-bold text-dark m-0">⚡ Promotional Campaigns</h3>
            <p className="text-muted small m-0 mt-1">
              Manage and monitor live platform offers, BOGOs, and tiered spend
              incentives.
            </p>
          </div>
          <div className="col-auto">
            <button
              className="btn btn-primary px-4 py-2 fw-semibold shadow-sm d-flex align-items-center gap-2"
              onClick={() => navigate("/changes-offers")}
            >
              <span>➕</span> Create New Offer
            </button>
          </div>
        </div>
      </div>

      {/* Main Table View */}
      <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light text-secondary border-bottom">
              <tr>
                <th className="py-3 ps-4" style={{ width: "25%" }}>
                  Campaign Details
                </th>
                <th className="py-3">Discount Architecture</th>
                <th className="py-3">Benefit Value</th>
                <th className="py-3">Valid From</th>
                <th className="py-3">Valid Till</th>
                <th className="py-3 text-end pe-4" style={{ width: "15%" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {!getOffers || getOffers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    <div className="fs-4 mb-2">📦</div>
                    No running marketing campaigns found.
                  </td>
                </tr>
              ) : (
                getOffers.map((offer) => (
                  <tr key={offer._id} className="border-bottom">
                    {/* Title & Desc */}
                    <td className="py-3 ps-4">
                      <div className="fw-semibold text-dark">{offer.title}</div>
                      {offer.description && (
                        <div
                          className="text-muted small text-truncate"
                          style={{ maxWidth: "250px" }}
                        >
                          {offer.description}
                        </div>
                      )}
                    </td>

                    {/* Type Badge */}
                    <td className="py-3">{renderTypeBadge(offer.offerType)}</td>

                    {/* Rendered Logic Values */}
                    <td className="py-3">{renderOfferValue(offer)}</td>

                    {/* Dates */}
                    <td className="py-3 text-secondary small">
                      {offer.activeFrom
                        ? new Date(offer.activeFrom).toLocaleDateString(
                            "en-IN",
                            { day: "2-digit", month: "short", year: "numeric" },
                          )
                        : "—"}
                    </td>
                    <td className="py-3 text-secondary small">
                      {offer.activeTo ? (
                        <span
                          className={
                            new Date(offer.activeTo) < new Date()
                              ? "text-danger fw-medium"
                              : ""
                          }
                        >
                          {new Date(offer.activeTo).toLocaleDateString(
                            "en-IN",
                            { day: "2-digit", month: "short", year: "numeric" },
                          )}
                          {new Date(offer.activeTo) < new Date() &&
                            " (Expired)"}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>

                    {/* Actions */}
                    <td className="py-3 text-end pe-4">
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary px-3 rounded-2"
                          onClick={() =>
                            navigate("/changes-offers", {
                              state: {
                                type: "edit",
                                productId: offer,
                              },
                            })
                          }
                        >
                          ✏️ Edit
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger px-2.5 rounded-2"
                          onClick={() => handleDelete(offer._id)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {loader && <FullScreenLoader />}
    </div>
  );
};

export default OfferList;
